import type { PlanDetailResponse, PlanListResponse } from '@rheora/enrollment-contract';
import {
  useQueries,
  useQuery,
  useQueryClient,
  type UseQueryResult,
} from '@tanstack/react-query';

import {
  disclosureFromListEntry,
  type PriceDisclosure,
} from '../services/price-disclosure.service';
import { fetchPlanDetail, fetchPlans, fetchPriceDisclosure } from './plans.fetchers';

/**
 * TanStack Query hooks for the storefront catalogue. Pages call THESE, never the
 * fetchers. Plan/price data is public and short-cached in the BFF, so a modest
 * stale window is enough. A 404 is surfaced as an error the page distinguishes
 * with `isNotFound` (bff-client) — no silent empty state.
 */

export const shopQueryKeys = {
  all: ['shop'] as const,
  /**
   * The shelf, per delivery utility.
   *
   * ⚠️ THE TDU IS PART OF THE KEY, AND MUST BE. The BFF narrows server-side, so
   * an ONCR response and an all-Texas response are DIFFERENT ANSWERS to
   * different questions. Sharing one key would let whichever page loaded first
   * decide what every other page sees — a CenterPoint shopper served Oncor's
   * shelf, which is the E-SF19 P1 defect that reached QA once already.
   *
   * `'*'` for the whole state rather than omitting the segment, so the two are
   * distinguishable rather than one being a prefix of the other.
   */
  plans: (tdu?: string) => ['shop', 'plans', tdu ?? '*'] as const,
  planDetail: (id: string) => ['shop', 'plans', 'detail', id] as const,
  pricePoints: (id: string) => ['shop', 'plans', 'price-points', id] as const,
} as const;

/** Exported so a PREFETCH of the same key cannot drift from its query. */
export const PLANS_STALE_MS = 60_000;

/**
 * The narrow slice of `QueryClient` the seeding needs. Structural, so the real
 * client satisfies it without a cast and a test can pass a recorder.
 */
export interface PricePointsCacheWriter {
  setQueryData(queryKey: readonly unknown[], data: PriceDisclosure): unknown;
}

/**
 * Publish the price points the LIST already carried into the per-plan cache
 * entries the cards read (QA-2026-08-05 BLOCKER 2).
 *
 * ── WHY SEED THE CACHE RATHER THAN CHANGE WHAT THE CARDS READ ──────────────
 * `PlanPriceHeadline` is the one component allowed to render "the price", and it
 * reads `usePricePointsQuery(planId)`. Rewiring ~4 call sites to pass the data
 * down would have worked and would have left two ways to obtain a price — which
 * is how the shelf and the detail page drifted apart once already (see
 * `MeterPlanCard`'s header). Instead the SAME cache key is populated from the
 * list, so every existing reader is satisfied from memory and issues no request.
 *
 * ⚠️ THE KEYS ARE `shopQueryKeys.pricePoints(id)`, DELIBERATELY AND EXACTLY.
 * That reuse is the entire mechanism: a private key would fill a cache nobody
 * reads and the ~20 per-card requests would all still go out.
 *
 * A plan the BFF could not carry points for is skipped, so its card falls back
 * to fetching its own — the pre-existing behaviour, for one card instead of all.
 *
 * ⚠️ A REFUSAL IS SEEDED TOO, NOT SKIPPED. A plan whose §25.475 average price the
 * BFF declines to publish arrives with `priceWithheld` instead of `pricePoints`,
 * and that is an ANSWER — cache it. Treating it as "nothing arrived" would send
 * all 8 of the live catalogue's refusing tariffs back to the BFF on every render,
 * to be refused identically each time: a fifth of the very fan-out this seeding
 * exists to remove, spent on a question already answered.
 *
 * @returns how many cards were seeded (the rest fall back).
 */
export function seedPricePointsCache(
  cache: PricePointsCacheWriter,
  response: PlanListResponse,
): number {
  let seeded = 0;
  for (const plan of response.plans) {
    const disclosure = disclosureFromListEntry(plan);
    if (disclosure === undefined) continue;
    cache.setQueryData(shopQueryKeys.pricePoints(plan.tariffId), disclosure);
    seeded += 1;
  }
  return seeded;
}

/**
 * The shelf list — and, as of the fan-out fix, the price for every card on it.
 *
 * One render used to cost this request PLUS one price-points request per plan:
 * ~21 against a 30/minute per-session budget, so a shopper who looked at the
 * shelf three times was rate-limited out of the storefront. The BFF now returns
 * the points inline and this hook publishes them under the cards' own cache
 * keys, so the render costs exactly one request.
 *
 * ── `tdu` NARROWS IT SERVER-SIDE ──────────────────────────────────────────
 * Pass the meter's TDU CODE and the BFF prices only that utility's tariffs. The
 * meter-first shelf used to fetch all of Texas and discard the rest in the
 * browser, after the BFF had already paid to price every one of them. Omit it
 * on the marketing surfaces, which genuinely want the whole catalogue.
 */
export function usePlansQuery(
  tdu?: string,
  options?: { readonly enabled?: boolean },
): UseQueryResult<PlanListResponse> {
  const queryClient = useQueryClient();
  return useQuery({
    queryKey: shopQueryKeys.plans(tdu),
    queryFn: async (): Promise<PlanListResponse> => {
      const response = await fetchPlans(tdu);
      seedPricePointsCache(queryClient, response);
      return response;
    },
    // ⚠️ `enabled` belongs at the CALL SITE, not derived from `tdu` here.
    // "No TDU" is a legitimate question on the marketing surfaces, which want
    // the whole catalogue — so an absent `tdu` must NOT disable the query. It is
    // only the meter-first shelf, which cannot answer until its meter resolves,
    // that needs to wait. Deriving it from `tdu` would silently break the
    // landing page's featured strip.
    enabled: options?.enabled ?? true,
    staleTime: PLANS_STALE_MS,
  });
}

/** One plan's detail. Disabled until an id is present. */
export function usePlanDetailQuery(id: string | undefined): UseQueryResult<PlanDetailResponse> {
  return useQuery({
    queryKey: shopQueryKeys.planDetail(id ?? ''),
    queryFn: () => fetchPlanDetail(id as string),
    enabled: id !== undefined && id.length > 0,
    staleTime: PLANS_STALE_MS,
  });
}

/**
 * What may be said about a plan's §25.475 average price — the shelf/detail
 * HEADLINE. Disabled until an id is present. Kept as its own query (not folded
 * into detail) so a card can headline the price without fetching full detail text.
 *
 * ⚠️ UNCHANGED BY THE FAN-OUT FIX, ON PURPOSE. On the shelf this hook is
 * answered from the cache `usePlansQuery` seeded — same key, same shape, no
 * request. It still FETCHES when there is nothing seeded: the plan-detail page
 * reached by deep link, and any card the BFF could not carry an answer for.
 * Making the shelf cheap must not make those paths impossible.
 *
 * ⚠️ `data` IS A DISCLOSURE, NOT A PRICE. `WITHHELD` is a successful answer — a
 * plan we deliberately do not publish an average price for — so it resolves
 * rather than rejecting. `isError` therefore still means what it always meant:
 * something went wrong, and a retry may help. Keeping those apart is the entire
 * point; collapsing them is what showed a customer "Price unavailable" on a plan
 * an agent had just offered them.
 */
export function usePricePointsQuery(id: string | undefined): UseQueryResult<PriceDisclosure> {
  return useQuery({
    queryKey: shopQueryKeys.pricePoints(id ?? ''),
    queryFn: () => fetchPriceDisclosure(id as string),
    enabled: id !== undefined && id.length > 0,
    staleTime: PLANS_STALE_MS,
  });
}

/**
 * Price disclosures for a WHOLE SHELF, as a `tariffId → disclosure` map. A plan
 * whose read is still pending or has FAILED is simply absent from the map; a plan
 * whose price is WITHHELD is present, carrying that answer — the difference
 * between "we do not know yet" and "we do not publish one".
 *
 * ── WHY THE SHELF NEEDS THESE AND NOT JUST THE CARDS ───────────────────────
 * The shelf is ordered by the §25.475 average price the CARD SHOWS at the shopper's
 * usage (`planOrdering.ts`), and that figure comes from the price-points endpoint —
 * so the page has to hold the same numbers its cards render.
 *
 * ⚠️ THIS ADDS NO REQUESTS. It reuses `shopQueryKeys.pricePoints(id)`, the exact key
 * `PlanPriceHeadline` already observes, so the cards read the same cache entries
 * rather than fetching again. The shelf's existing N+1 against the 30/min session
 * limiter (QA-2026-07-31-07) is neither improved nor worsened here; fixing it needs
 * a batch endpoint on the BFF and is out of scope for this change.
 */
export function usePricePointsForPlans(
  ids: readonly string[],
): ReadonlyMap<string, PriceDisclosure> {
  const results = useQueries({
    queries: ids.map((id) => ({
      queryKey: shopQueryKeys.pricePoints(id),
      queryFn: () => fetchPriceDisclosure(id),
      staleTime: PLANS_STALE_MS,
    })),
    combine: (queries) => {
      const byId = new Map<string, PriceDisclosure>();
      queries.forEach((query, index) => {
        const id = ids[index];
        if (id !== undefined && query.data !== undefined) byId.set(id, query.data);
      });
      return byId;
    },
  });
  return results;
}
